import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Heading, Link, Spinner } from '@printdeal/ui-library';
// @ts-expect-error
import { accountDetailsSchema } from '@printdeal/validation';
import { FormikProps, useFormik } from 'formik';
import { Link as GatsbyLink } from 'gatsby';

import Breadcrumb from '../../../Breadcrumb';

import { messages } from '../../translations/account';
import AccountForm, { AccountFormValues, FeedbackValues } from './AccountForm';
import UpdatePasswordForm from './UpdatePasswordForm';
// @ts-expect-error
import { actions as customerActions, selectors as customerSelectors } from '../../../../store/slices/customerSlice';

import Head from '../../../PrivateHead';
import { IAccount } from '../../../../services/customer/CustomerService';
import { RootState } from '../../../../store';
import { ServiceFactory } from '../../../../services/ServiceFactory';
import { getConfiguration } from '../../../../helpers/Configuration';
import { Customer } from '../../../../types/Customer';

const STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
};

const AccountDetailsPage = () => {
  const customer = useSelector<RootState, Customer>((state) => customerSelectors.getCustomerData(state));
  const { companyName, vatId, custom } = customer;
  const intl = useIntl();
  const dispatch = useDispatch();
  const authToken = useSelector<RootState, string | undefined>(({ auth }) => auth.token);
  const configuration = getConfiguration();

  const customerApi = ServiceFactory.createCustomerService();
  const [accountFeedback, setAccountFeedback] = useState<FeedbackValues | undefined>(undefined);
  const [status, setStatus] = useState(STATUS.LOADING);
  const [account, setAccount] = useState<AccountFormValues>({
    locale: '',
    value: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    newsletterOptIn: false,
    remarks: '',
    company: companyName,
    cocNumber: custom.fields.cocNumber,
    vatId,
    isCompany: String(custom.fields.isCompany),
  });

  const fetchAccount = async () => {
    const remoteAccount = await customerApi.getAccount();
    setAccount({ ...account, ...remoteAccount });
    setStatus(STATUS.LOADED);
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  const updateAccount = async (values: IAccount, formik: FormikProps<AccountFormValues>) => {
    dispatch(customerActions.updateAccountRequest());
    try {
      const updatedAccountPayload = await customerApi.updateAccount(values);
      setAccountFeedback({ kind: 'success', message: intl.formatMessage(messages.success) });
      formik.setTouched({});
      dispatch(customerActions.updateAccountSuccess(updatedAccountPayload));
    } catch (error) {
      setAccountFeedback({ kind: 'error', message: intl.formatMessage(messages.error) });
      dispatch(customerActions.updateAccountFailure({ error }));
    }
  };

  // If the customer is a company without a name, allow edits
  const allowEmptyCompany = custom?.fields?.isCompany && !companyName;
  const accountFormik = useFormik({
    initialValues: account,
    validationSchema: accountDetailsSchema({
      vatId: {
        url: process.env.GATSBY_VAT_ID_VALIDATION_URL,
        token: authToken,
      },
      allowEmptyCompany,
    }),
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      const updates = { ...values, isCompany: (values.isCompany === 'true'), companyName: values.company };
      await updateAccount(updates, accountFormik);
    },
  });

  return (
    <div className="data-sentry-block">
      <Head title={intl.formatMessage(messages.accountDetails)} />

      <Breadcrumb className="pd-mb-4">
        <Breadcrumb.Item to="..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="account.details" defaultMessage="Change Account details" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <section>
        <Heading as="h1" className="pd-mb-6">
          <FormattedMessage id="account.details" defaultMessage="Change Account details" />
        </Heading>

        {status === STATUS.LOADING && (
          <div className="pd-my-4 pd-text-center">
            <Spinner
              variant="primary"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          </div>
        )}

        {status === STATUS.LOADED && (
          <>
            <section className="pd-mb-6">
              <AccountForm
                formik={accountFormik}
                feedback={accountFeedback}
                locales={configuration.locales}
                allowEmptyCompany={allowEmptyCompany}
              />
            </section>
            <UpdatePasswordForm />
          </>
        )}
      </section>
      <footer className="pd-my-7">
        <Link as={GatsbyLink} to="../" variant="secondary" iconBefore="chevron-left">
          <FormattedMessage id="account.linkToAccount" defaultMessage="Link to account" />
        </Link>
      </footer>
    </div>
  );
};

export default AccountDetailsPage;
